import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  fetchDecisionInstitutions,
  fetchDecisionProducts,
  fetchDecisionProduct,
  getDecisionApiError
} from '../../services/decisionApi';
import BenchmarkPanel from './components/BenchmarkPanel';
import NextDayOutlookPanel from './components/NextDayOutlookPanel';
import DecisionChecklistPanel from './components/DecisionChecklistPanel';
import ScenarioOutlookPanel from './components/ScenarioOutlookPanel';
import InsightWallPanel from './components/InsightWallPanel';
import DocumentInsightPanel from './components/DocumentInsightPanel';
import RiskExposurePanel from './components/RiskExposurePanel';
import ProductDataLedgerPanel from './components/ProductDataLedgerPanel';
import ProductOverviewTab from './components/ProductOverviewTab';
import ProductPerformanceTab from './components/ProductPerformanceTab';
import MarketFactorPanel from './components/MarketFactorPanel';
import ProductMarketSensitivityPanel from './components/ProductMarketSensitivityPanel';
import MarketPressureTransmissionPanel from './components/MarketPressureTransmissionPanel';
import EvidenceConsistencyPanel from './components/EvidenceConsistencyPanel';
import { useDecisionPortfolio } from './DecisionPortfolioContext';

const PRODUCT_TABS = [
  { key: 'overview', label: '总览与结论', short: '总览' },
  { key: 'performance', label: '产品表现', short: '表现' },
  { key: 'disclosure', label: '穿透与持仓', short: '穿透' },
  { key: 'market', label: '市场与情景', short: '市场' },
  { key: 'risk', label: '风险与决策', short: '决策' },
  { key: 'data', label: '数据底稿', short: '数据' }
];

const TAB_KEYS = new Set(PRODUCT_TABS.map((item) => item.key));

function ProductTabNav({ activeTab, onChange }) {
  return (
    <div className="product-tab-nav" role="tablist" aria-label="产品分析分区">
      {PRODUCT_TABS.map((item, index) => (
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === item.key}
          className={activeTab === item.key ? 'active' : ''}
          onClick={() => onChange(item.key)}
          key={item.key}
        >
          <span>{String(index + 1).padStart(2, '0')}</span>
          <strong>{item.label}</strong>
        </button>
      ))}
    </div>
  );
}

function SectionCard({ title, subtitle, className = '', children }) {
  return (
    <div className={`decision-card decision-grid-span-2 tab-section-card ${className}`}>
      <div className="tab-section-head">
        <h3>{title}</h3>
        {subtitle ? <p>{subtitle}</p> : null}
      </div>
      {children}
    </div>
  );
}

function DisclosureTab({ result }) {
  return (
    <div className="decision-grid decision-grid-2 product-tab-panel" role="tabpanel">
      <SectionCard
        title="产品穿透画像"
        subtitle="人工审核发布的说明书、最新运作报告、实际资产配置与前十大持仓"
        className="document-priority-card"
      >
        <DocumentInsightPanel disclosure={result.product_disclosure || result.document_insight} />
      </SectionCard>
    </div>
  );
}

function MacroContext({ result }) {
  const macro = result.dimensions?.macro || {};
  const snapshots = result.benchmark_analysis?.index_snapshots || [];
  const available = snapshots.filter((item) => item.change_pct != null).length;
  return (
    <div className="market-context-strip">
      <div><span>宏观匹配</span><strong>{macro.score != null ? `${macro.score}/100` : '—'}</strong><small>{macro.signals?.[0] || '暂无宏观信号'}</small></div>
      <div><span>基准行情覆盖</span><strong>{available}/{snapshots.length || 0}</strong><small>已取得行情的基准成分</small></div>
      <div><span>合成基准当日</span><strong>{result.benchmark_analysis?.synthetic_return_today_text || '—'}</strong><small>{result.benchmark_analysis?.data_note || '暂无说明'}</small></div>
      <div><span>产品相对基准</span><strong>{result.benchmark_analysis?.vs_synthetic_bp != null ? `${Number(result.benchmark_analysis.vs_synthetic_bp).toFixed(2)}bp` : '—'}</strong><small>{result.benchmark_analysis?.comparison_date_aligned ? `${result.benchmark_analysis.market_trade_date} 行情 → ${result.benchmark_analysis.product_change_date} 净值` : `按${result.benchmark_analysis?.confirm_time || 'T+N'}应对应 ${result.benchmark_analysis?.expected_nav_date || '—'} 净值`}</small></div>
    </div>
  );
}

function MarketGuide({ conclusion, use, fixed = true }) {
  return (
    <div className="performance-reading-guide market-reading-guide">
      <div><span>{fixed ? '系统固定结论' : '情景解释'}</span><p>{conclusion}</p></div>
      <div><span>你应该怎么用</span><p>{use}</p></div>
    </div>
  );
}

function MarketTab({ result }) {
  return (
    <div className="decision-grid decision-grid-2 product-tab-panel" role="tabpanel">
      <SectionCard title="当前市场环境" subtitle="现阶段已接入的债市状态和基准成分行情">
        <MarketGuide
          conclusion={result.market_factor_context?.summary || '市场因子数据不足，暂不能形成固定结论。'}
          use="先看与本产品标记为“直接相关”或“高度相关”的因子；观察项不要机械加入产品判断。市场因子连续走弱且产品净值同步恶化，才是更强的减仓证据。"
        />
        <MacroContext result={result} />
        <MarketFactorPanel context={result.market_factor_context} />
      </SectionCard>
      <SectionCard title="市场压力如何传导到本产品" subtitle="将已披露暴露与公共市场环境连接，解释买入、加仓、持有和减仓影响">
        <MarketPressureTransmissionPanel transmission={result.market_pressure_transmission} />
      </SectionCard>
      <SectionCard title="产品—市场历史响应" subtitle="按T+N交易日历对齐，识别哪些公共市场因子与产品净值曾经联动">
        <MarketGuide
          fixed={false}
          conclusion={result.product_market_sensitivity?.summary || '历史对齐样本不足，暂不判断产品对市场的敏感度。'}
          use="先看样本数，再看相关方向、强度与方向一致率。只有样本较多且关系较稳定的因子，才适合用来解释下一个净值；任何单一系数都不应直接触发买卖。"
        />
        <ProductMarketSensitivityPanel sensitivity={result.product_market_sensitivity} />
      </SectionCard>
      <SectionCard title="业绩比较基准" subtitle="基准拆解、行情归因、相对表现与压力敏感度">
        <MarketGuide
          conclusion={result.benchmark_analysis?.comparison_date_aligned
            ? `${result.benchmark_analysis.market_trade_date} 行情按${result.benchmark_analysis.confirm_time || 'T+N'}和交易日历，对应 ${result.benchmark_analysis.product_change_date} 净值，已完成因果对齐。`
            : `${result.benchmark_analysis?.market_trade_date || '—'} 行情按${result.benchmark_analysis?.confirm_time || 'T+N'}应影响 ${result.benchmark_analysis?.expected_nav_date || '—'} 净值；当前最新净值为 ${result.benchmark_analysis?.product_change_date || '—'}，暂不作相对表现结论。`}
          use="基准回答产品理论上应该跟随什么市场。看绝对收益之前，先确认产品是否持续跑赢或跑输基准；单日偏离意义有限，5日和20日同向偏离更值得关注。"
        />
        <BenchmarkPanel
          analysis={result.benchmark_analysis}
          sensitivity={result.benchmark_sensitivity}
          stress={result.benchmark_stress}
        />
      </SectionCard>
      <SectionCard title="待披露净值影响参考" subtitle="明确哪一天市场行情影响哪一个净值标签日">
        <MarketGuide
          fixed={false}
          conclusion="这是从市场行情向受影响净值日做的区间推演，不是先有净值、后找行情，也不是已经披露的固定事实。"
          use="先看“行情日 → 影响净值日”的映射，再用真实净值披露结果复核偏差；区间参考不应单独触发买卖。"
        />
        <NextDayOutlookPanel outlook={result.next_day_outlook} />
      </SectionCard>
      <SectionCard title="未来 5 个交易日情景" subtitle="乐观、中性和悲观压力情景，不是精确净值预测">
        <MarketGuide
          fixed={false}
          conclusion="三种情景是压力边界，不是上涨概率，也不存在系统认定一定发生的某一种情景。"
          use="用悲观情景检查自己是否能承受潜在回撤，用中性情景管理收益预期；只有实际市场逐步接近悲观驱动因素时，才提高减仓优先级。"
        />
        <ScenarioOutlookPanel outlook={result.scenario_outlook} />
      </SectionCard>
    </div>
  );
}

function Alternatives({ comparison }) {
  const analysis = comparison?.alternatives;
  const rows = analysis?.items || [];
  if (!analysis?.available || !rows.length) {
    return (
      <div className="strict-alternative-empty">
        <strong>暂不推荐替代产品</strong>
        <p>{analysis?.reason || '严格同类样本不足。'}</p>
        {analysis?.boundary_label ? <small>{analysis.boundary_label} · {analysis.sample_size || 0}个产品</small> : null}
      </div>
    );
  }
  return (
    <div className="strict-alternative-panel">
      <div className="strict-alternative-boundary"><span>采用边界</span><strong>{analysis.boundary_label}</strong><small>{analysis.sample_size}个可比产品</small></div>
      <div className="strict-alternative-grid">
        {rows.map((alt) => (
          <article key={alt.product_id}>
            <div><span>候选产品</span><strong>{alt.product_name}</strong><small>ID {alt.product_id} · {alt.wins}/{alt.valid_metrics}项领先</small></div>
            <ul>{(alt.metrics || []).map((metric) => (
              <li key={metric.key}><span>{metric.label}</span><strong>{Number(metric.candidate_value).toFixed(2)}{metric.suffix}</strong><em className={metric.better ? 'is-better' : ''}>{metric.gap > 0 ? '+' : ''}{Number(metric.gap).toFixed(2)}{metric.suffix}</em></li>
            ))}</ul>
          </article>
        ))}
      </div>
      <p className="strict-alternative-caveat">{analysis.caveat}</p>
    </div>
  );
}

function RiskTab({ result }) {
  return (
    <div className="decision-grid decision-grid-2 product-tab-panel" role="tabpanel">
      <SectionCard
        title="六类风险暴露与操作影响"
        subtitle="利率、信用、权益、流动性、杠杆和集中度；区分披露、计算、推断与缺失"
        className="exposure-priority-card"
      >
        <RiskExposurePanel profile={result.risk_exposure_profile} />
      </SectionCard>
      <SectionCard title="证据一致性" subtitle="同时呈现支持、谨慎、分化与缺失证据，不因单一亮点或风险直接下结论">
        <EvidenceConsistencyPanel analysis={result.evidence_consistency} />
      </SectionCard>
      <SectionCard title="决策清单" subtitle="买入、持有和减仓条件的规则化核对">
        <DecisionChecklistPanel checklist={result.decision_checklist} />
      </SectionCard>
      <SectionCard title="洞察墙" subtitle="汇总产品表现、基准、宏观、持仓和披露信号">
        <InsightWallPanel insights={result.insights} />
      </SectionCard>
      <SectionCard title="严格同类替代候选" subtitle="同系列、同风险、同期限和同确认时滞均满足后，才比较替代机会">
        <Alternatives comparison={result.strict_peer_comparison} />
      </SectionCard>
    </div>
  );
}

function DataTab({ result }) {
  return (
    <div className="decision-card data-ledger-card product-tab-panel" role="tabpanel">
      <div className="tab-section-head">
        <h3>产品数据底稿</h3>
        <p>完整核对现有字段、性质、来源、数据日期、原文证据和缺失项</p>
      </div>
      <ProductDataLedgerPanel result={result} />
    </div>
  );
}

function ActiveProductTab({ activeTab, result }) {
  if (activeTab === 'performance') return <ProductPerformanceTab result={result} />;
  if (activeTab === 'disclosure') return <DisclosureTab result={result} />;
  if (activeTab === 'market') return <MarketTab result={result} />;
  if (activeTab === 'risk') return <RiskTab result={result} />;
  if (activeTab === 'data') return <DataTab result={result} />;
  return <ProductOverviewTab result={result} />;
}

export default function ProductDecisionPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialId = searchParams.get('id') || '';
  const initialAgent = searchParams.get('agent') || '';
  const requestedTab = searchParams.get('tab') || (searchParams.get('view') === 'data' ? 'data' : 'overview');
  const { upsertResult } = useDecisionPortfolio();

  const [institutions, setInstitutions] = useState([]);
  const [institution, setInstitution] = useState(initialAgent);
  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState(initialId);
  const [result, setResult] = useState(null);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.has(requestedTab) ? requestedTab : 'overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const analyze = useCallback(async (pid) => {
    const id = String(pid || productId || '').trim();
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchDecisionProduct(id);
      setResult(data.result);
      if (data.result) {
        try {
          upsertResult(data.result);
        } catch (_) {
          /* Provider 外打开时忽略 */
        }
      }
    } catch (err) {
      setError(getDecisionApiError(err));
      setResult(null);
    } finally {
      setLoading(false);
    }
  }, [productId, upsertResult]);

  useEffect(() => {
    fetchDecisionInstitutions()
      .then((rows) => setInstitutions(rows || []))
      .catch(() => setInstitutions([]));
  }, []);

  useEffect(() => {
    if (!institution) {
      setProducts([]);
      return;
    }
    fetchDecisionProducts(institution)
      .then((rows) => setProducts(rows || []))
      .catch(() => setProducts([]));
  }, [institution]);

  useEffect(() => {
    if (!initialId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const data = await fetchDecisionProduct(initialId);
        if (cancelled) return;
        setResult(data.result);
        if (data.result?.agent_org && !institution) setInstitution(data.result.agent_org);
        setProductId(initialId);
      } catch (err) {
        if (!cancelled) setError(getDecisionApiError(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [initialId]);

  function paramsFor(tab = activeTab) {
    const params = new URLSearchParams();
    if (productId) params.set('id', productId);
    if (institution) params.set('agent', institution);
    if (tab !== 'overview') params.set('tab', tab);
    return params;
  }

  function onInstitutionChange(value) {
    setInstitution(value);
    setProductId('');
    setResult(null);
    const params = new URLSearchParams();
    if (activeTab !== 'overview') params.set('tab', activeTab);
    setSearchParams(params);
  }

  function onProductChange(value) {
    setProductId(value);
    const params = new URLSearchParams();
    if (value) params.set('id', value);
    if (institution) params.set('agent', institution);
    if (activeTab !== 'overview') params.set('tab', activeTab);
    setSearchParams(params);
    if (value) analyze(value);
  }

  function onTabChange(tab) {
    setActiveTab(tab);
    setSearchParams(paramsFor(tab));
  }

  return (
    <div>
      <h1 className="decision-page-title">产品分析</h1>
      <p className="decision-page-sub">从快速结论进入产品表现、穿透持仓、市场情景、风险决策和完整数据底稿。</p>

      <div className="decision-card product-selector-card">
        <div className="decision-grid decision-grid-2">
          <label>
            <div className="decision-muted">代销机构</div>
            <select className="decision-select" value={institution} onChange={(event) => onInstitutionChange(event.target.value)}>
              <option value="">请选择</option>
              {institutions.map((name) => <option key={name} value={name}>{name}</option>)}
            </select>
          </label>
          <label>
            <div className="decision-muted">产品</div>
            <select className="decision-select" value={productId} onChange={(event) => onProductChange(event.target.value)}>
              <option value="">请选择</option>
              {products.map((product) => (
                <option key={product.product_id} value={product.product_id}>{product.product_name} ({product.product_id})</option>
              ))}
            </select>
          </label>
        </div>
        <button type="button" className="decision-btn" onClick={() => analyze()} disabled={!productId || loading}>
          {loading ? '分析中…' : '刷新分析'}
        </button>
        {result?.analyzed_at ? (
          <p className="decision-muted" style={{ marginTop: 8 }}>
            本次分析时间{' '}
            {new Date(result.analyzed_at).toLocaleString('zh-CN', { hour12: false })}
          </p>
        ) : null}
        {error ? <p className="decision-error">{error}</p> : null}
      </div>

      {result ? (
        <>
          <ProductTabNav activeTab={activeTab} onChange={onTabChange} />
          <ActiveProductTab activeTab={activeTab} result={result} />
        </>
      ) : null}
    </div>
  );
}
